/* eslint-disable react/no-this-in-sfc */
import format from "date-fns/format";
import Highcharts, { SeriesSankeyPointOptionsObject } from "highcharts";
import { HighchartsReactRefObject } from "highcharts-react-official";
import { useMemo, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { useAppVariant } from "@jugl-web/utils";
import { normalizeChartValue } from "../../utils";
import { WidgetTooltip } from "../WidgetTooltip";
import { SentimentAnalysisDataEntry, SentimentAnalysisNode } from "./types";

type SankeyTooltipPoint = Highcharts.Point &
  (
    | {
        isNode: true;
        sum: number;
      }
    | {
        isNode: false;
        fromNode: SankeyTooltipPoint;
        toNode: SankeyTooltipPoint;
        weight: number;
      }
  );

export const useSentimentAnalysisChart = (
  data: SentimentAnalysisDataEntry[]
) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const { isMobile } = useAppVariant();

  const nodeById = useMemo(() => {
    const dict: Record<string, SentimentAnalysisNode> = {};

    data.forEach(([from, to]) => {
      dict[from.name] = from;
      dict[to.name] = to;
    });

    return dict;
  }, [data]);

  const nodes = useMemo(
    () =>
      Object.values(nodeById).map((node) => ({
        id: node.name,
        name: node.name,
        dataLabels: {
          style: {
            color: "#1A2023",
            fontSize: isMobile ? "11px" : "15px",
            fontWeight: node.isBold ? (isMobile ? "500" : "600") : "400",
          },
        },
      })),
    [isMobile, nodeById]
  );

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: "sankey",
        spacing: [0, 0, 0, 0],
        style: {
          fontFamily: "Poppins",
        },
      },
      title: {
        text: undefined,
      },
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      tooltip: {
        useHTML: true,
        followPointer: false,
        followTouchMove: false,
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: { whiteSpace: "normal" },
        formatter() {
          const point = this as SankeyTooltipPoint;

          const title = point.isNode
            ? point.name
            : `${point.fromNode.name} → ${point.toNode.name}`;

          const subtitle = format(new Date(), "MMMM, yyyy");

          const value = Math.round(
            (point.isNode ? point.sum : point.weight) ?? 0
          );

          const content = (
            <div>
              <span className="text-dark text-base font-semibold">
                {value ?? 0}
              </span>{" "}
              <span className="text-dark-900 font-sm font-normal">
                {value === 1 ? "session" : "sessions"}
              </span>
            </div>
          );

          return ReactDOMServer.renderToStaticMarkup(
            <WidgetTooltip
              title={title}
              subtitle={subtitle}
              content={content}
            />
          );
        },
      },
      series: [
        {
          type: "sankey",
          name: "Flow",
          nodes,
          data: data.map<SeriesSankeyPointOptionsObject>(
            ([from, to, weight]) => ({
              from: from.name,
              to: to.name,
              weight: normalizeChartValue(weight, isMobile),
            })
          ),
        },
      ],
      plotOptions: {
        series: {
          animation: false,
        },
        sankey: {
          curveFactor: 0.3,
          nodeWidth: 40,
          nodePadding: 20,
          borderWidth: 0,
          linkOpacity: 0.45,
          opacity: 1,
          states: {
            hover: {
              linkOpacity: 0.7,
            },
          },
          minLinkWidth: 10,
          dataLabels: {
            enabled: true,
            inside: true,
            verticalAlign: "bottom",
            align: "center",
            x: 0,
            y: 0,
            allowOverlap: false,
            overflow: "justify",
            position: "center",
            style: {
              fontSize: isMobile ? "11px" : "15px",
            },
          },
        },
      },
    }),
    [data, isMobile, nodes]
  );

  const isEmpty = data.length === 0;

  return {
    chartRef,
    options,
    isEmpty,
  };
};
